import React, { useState, useEffect, useContext } from 'react';
import {
    LayoutDashboard, MapPin,
    HelpCircle, Menu, X,
    ChevronDown, LogOut, Ticket, CalendarDays,
    Smartphone, Compass, Briefcase, ShieldCheck, MessageSquare, Lock as LockIcon
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { voucherService } from '../../services/vouchers/voucherService';
import { appBookingService } from '../../services/appBookings/appBookingService';
import DashboardContext from '../../context/dashboard/DashboardContext';
import TrripLogo from '../../assets/Trrip_logo/Trrip_logo_1.png';
import PlanGate from '../../components/gating/PlanGate';
import { useSubscription } from '../../context/subscription/SubscriptionContext';

const MobileNav = ({ activeTab, setActiveTab }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const dashboardContext = useContext(DashboardContext);
    const stats = dashboardContext?.stats;

    const [counts, setCounts] = useState({
        vouchers: stats?.totalVouchers ?? 0,
        bookings: stats?.totalBookings ?? 0
    });

    useEffect(() => {
        if (stats) {
            setCounts((prev) => ({
                vouchers: stats.totalVouchers !== undefined ? stats.totalVouchers : prev.vouchers,
                bookings: stats.totalBookings !== undefined ? stats.totalBookings : prev.bookings
            }));
        }
    }, [stats]);

    useEffect(() => {
        const fetchLiveCounts = async () => {
            try {
                const [vouchersRes, bookingsRes] = await Promise.all([
                    voucherService.getVouchers().catch(() => null),
                    appBookingService.getAppBookings().catch(() => null)
                ]);

                setCounts((prev) => {
                    const newVouchers = Array.isArray(vouchersRes)
                        ? vouchersRes.length
                        : (Array.isArray(vouchersRes?.vouchers) ? vouchersRes.vouchers.length : (Array.isArray(vouchersRes?.data) ? vouchersRes.data.length : prev.vouchers));

                    const bookingsData = bookingsRes?.data || bookingsRes;
                    const newBookings = Array.isArray(bookingsData) ? bookingsData.length : prev.bookings;

                    return {
                        vouchers: newVouchers,
                        bookings: newBookings
                    };
                });
            } catch (e) {
                // silent
            }
        };

        fetchLiveCounts();
    }, [location.pathname, isDrawerOpen]);

    const { subscription } = useSubscription();
    const isEnterprisePlan = subscription && 
        subscription.status === 'active' && 
        subscription.plan_id && 
        (subscription.plan_id.slug === 'enterprise' || subscription.plan_id.name?.toLowerCase().includes('enterprise'));

    const subUserRole = localStorage.getItem('sub_user_role');
    const isBookingsRole = subUserRole === 'bookings';
    const isOperationsRole = subUserRole === 'operations';
    const canManageRoles = isEnterprisePlan && subUserRole !== 'bookings' && subUserRole !== 'operations';



    // Keep the original scroll to hide behavior
    useEffect(() => {
        const mainEl = document.querySelector('main');
        const handleScroll = (e) => {
            const currentScrollY = e?.target?.scrollTop !== undefined ? e.target.scrollTop : window.scrollY;

            if (currentScrollY > lastScrollY && currentScrollY > 20) {
                setIsVisible(false);
            } else {
                setIsVisible(true);
            }

            setLastScrollY(currentScrollY);
        };

        if (mainEl) {
            mainEl.addEventListener('scroll', handleScroll, { passive: true });
        }
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            if (mainEl) mainEl.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [lastScrollY]);

    // Lock body scroll when drawer is open
    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isDrawerOpen]);

    const handleLogout = () => {
        localStorage.removeItem('token_agents');
        localStorage.removeItem('agent_id');
        localStorage.removeItem('user_id');
        sessionStorage.clear();
        toast('Logged out successfully');
        navigate('/login');
    };

    const bottomItems = [
        { id: 'home', label: 'Home', icon: LayoutDashboard, path: '/dashboard' },
        { id: 'voucher', label: 'Vouchers', icon: Ticket, path: '/voucher' },
        { id: 'bookings', label: 'Bookings', icon: CalendarDays, path: '/bookings' },
        { id: 'feedback', label: 'Feedback', icon: MessageSquare, path: '/feed-back' },
        { id: 'menu', label: 'Menu', icon: Menu, isToggle: true },
    ];

    const navigationSections = [
        {
            title: 'ON-TRRIP',
            items: [
                { id: 'home', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
                ...(!isOperationsRole ? [{
                    id: 'voucher',
                    label: 'Vouchers',
                    icon: Ticket,
                    path: '/voucher',
                    count: counts.vouchers
                }] : []),
                {
                    id: 'bookings',
                    label: 'App bookings',
                    icon: CalendarDays,
                    path: '/bookings',
                    count: counts.bookings
                },
                ...(!isBookingsRole ? [{
                    id: 'tracking',
                    label: 'Tracking tools',
                    icon: MapPin,
                    path: '/tracking'
                }] : [])
            ]
        },
        {
            title: 'GROW',
            items: [
                ...(!isBookingsRole ? [{
                    id: 'post-connect',
                    label: 'Post Trrip Connect',
                    icon: Smartphone,
                    path: '/post-connect'
                }] : []),
                ...(!isBookingsRole ? [{
                    id: 'partners',
                    label: 'Partners',
                    icon: Briefcase,
                    path: '/partners'
                }] : [])
                /* Temporarily locked for all plans & hidden from sidebar - kept for future release
                , ...(!isBookingsRole ? [{
                    id: 'your-itinerary',
                    label: 'Itinerary',
                    icon: Compass,
                    path: '/your-itinerary'
                }] : [])
                */
            ]
        },
        {
            title: 'MANAGE',
            items: [
                ...(canManageRoles ? [{
                    id: 'role-management',
                    label: 'Team & roles',
                    icon: ShieldCheck,
                    path: '/role-management'
                }] : []),
                {
                    id: 'feed-back',
                    label: 'Traveller feedback',
                    icon: MessageSquare,
                    path: '/feed-back'
                },
                {
                    id: 'support',
                    label: 'Support',
                    icon: HelpCircle,
                    path: '/support'
                }
            ]
        }
    ];

    const handleNavClick = (path, id) => {
        setIsDrawerOpen(false);
        if (setActiveTab && id) setActiveTab(id);
        navigate(path);
    };

    const isPathActive = (item) => {
        if (!item.path) return false;
        if (item.path === '/dashboard') {
            return location.pathname === '/' || location.pathname === '/dashboard';
        }
        if (item.path === '/voucher') {
            return location.pathname.startsWith('/voucher') || location.pathname === '/create-voucher';
        }
        if (item.path === '/tracking') {
            return location.pathname.startsWith('/tracking');
        }
        if (item.path === '/your-itinerary') {
            return location.pathname === '/your-itinerary' || location.pathname === '/predefined-itinerary';
        }
        if (item.path === '/feed-back') {
            return location.pathname === '/feed-back' || location.pathname === '/feedback';
        }
        return location.pathname.startsWith(item.path);
    };

    return (
        <>
            {/* Mobile Floating Bottom Bar */}
            <div
                className={`min-[950px]:hidden fixed bottom-4 left-1/2 -translate-x-1/2 w-[90%] max-w-sm z-[90] transition-all duration-300 ease-in-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0'
                    }`}
            >
                <nav className="bg-white/95 backdrop-blur-2xl border border-slate-200/90 shadow-[0_8px_25px_rgba(0,0,0,0.12)] rounded-2xl flex items-center justify-around p-1 ring-1 ring-black/5">
                    {bottomItems.map((item) => {
                        const Icon = item.icon || LayoutDashboard;
                        const isMenuBtn = item.isToggle;
                        const isActive = isMenuBtn
                            ? isDrawerOpen
                            : (location.pathname === item.path ||
                                (item.id === 'home' && location.pathname === '/'));

                        return (
                            <button
                                key={item.id}
                                onClick={() => {
                                    if (isMenuBtn) {
                                        setIsDrawerOpen(!isDrawerOpen);
                                    } else {
                                        handleNavClick(item.path, item.id);
                                    }
                                }}
                                className="flex flex-1 flex-col items-center justify-center py-1.5 px-1 rounded-xl transition-all duration-200 group relative"
                            >
                                <div
                                    className={`p-1.5 rounded-xl transition-all duration-200 ${isActive
                                            ? 'bg-[#067978]/10 text-[#067978] scale-105'
                                            : 'text-slate-400 group-hover:text-slate-600 group-hover:bg-slate-50'
                                        }`}
                                >
                                    <Icon
                                        size={18}
                                        strokeWidth={isActive ? 2.2 : 1.8}
                                        className="transition-transform duration-200"
                                    />
                                </div>

                                <span
                                    className={`text-[10px] tracking-tight transition-colors duration-200 ${isActive
                                            ? 'font-medium text-[#067978]'
                                            : 'font-normal text-slate-500 group-hover:text-slate-700'
                                        }`}
                                >
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Mobile Drawer Overlay */}
            {isDrawerOpen && (
                <div
                    onClick={() => setIsDrawerOpen(false)}
                    className="min-[950px]:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[100] transition-opacity duration-300"
                />
            )}

            {/* Mobile Drawer Content */}
            <div
                className={`min-[950px]:hidden fixed top-0 left-0 bottom-0 w-[82%] max-w-xs bg-white z-[101] shadow-2xl flex flex-col transition-transform duration-300 ease-out transform ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
            >
                {/* Drawer Header */}
                <div className="bg-[#067978] p-5 text-white flex items-center justify-between shadow-md relative overflow-hidden flex-shrink-0">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

                    <div className="flex items-center gap-3 relative z-10">
                        <img
                            src={TrripLogo}
                            alt="Trrip Logo"
                            className="h-11 w-auto object-contain drop-shadow-sm"
                        />
                        <div className="hidden min-[375px]:block w-[1px] h-8 bg-white/30 rounded-full"></div>
                        <div className="hidden min-[375px]:flex flex-col justify-center">
                            <span className="text-sm font-medium text-white tracking-tight leading-none">Company Portal</span>
                            <span className="text-[9px] text-teal-100/80 font-normal tracking-wide mt-1">Your On Tour Partner</span>
                        </div>
                    </div>

                    <button
                        onClick={() => setIsDrawerOpen(false)}
                        className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors relative z-10"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Drawer Navigation Options List */}
                <nav className="flex-1 p-3.5 space-y-4 overflow-y-auto no-scrollbar">
                    {navigationSections.map((section, idx) => (
                        <div key={section.title || idx} className="space-y-1">
                            {/* Section Header */}
                            {section.title && (
                                <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider px-3 pt-2 pb-1">
                                    {section.title}
                                </div>
                            )}

                            <div className="space-y-1">
                                {section.items.map((item) => {
                                    const Icon = item.icon || LayoutDashboard;



                                    const isActive = isPathActive(item);

                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() => handleNavClick(item.path, item.id)}
                                            className={`flex items-center w-full gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-normal transition-all duration-200 group ${isActive
                                                    ? 'bg-[#067978] text-white shadow-xs font-medium'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#067978]'
                                                }`}
                                        >
                                            <Icon size={18} className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-[#067978]'} transition-colors`} />
                                            <span>{item.label}</span>

                                            {/* Live Count Badge (Vouchers, Bookings) */}
                                            {item.count !== undefined && (
                                                <span className={`ml-auto text-xs font-normal ${isActive ? 'text-white/90' : 'text-slate-400'
                                                    }`}>
                                                    {item.count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>
            </div>
        </>
    );
};

export default MobileNav;
